import React from 'react';

import { observer } from 'mobx-react';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { getErrorMessage } from 'utils/api/errors';

import { useStore } from 'utils/hooks/store';

import { MembershipMode } from 'utils/api/modules/memberGroups';

interface ConvertToManualModalProps {
  open: boolean;
  onClose: () => void;
}

const ConvertToManualModal: React.FunctionComponent<ConvertToManualModalProps> = observer(({ open, onClose }) => {
  const { memberGroupDetails, notification } = useStore();
  const [saving, setSaving] = React.useState(false);
  const count = memberGroupDetails.group?.memberCount ?? 0;

  const description =
    count === 0
      ? 'This group is empty. Membership will no longer update automatically as users’ details change, and the rule will be removed.'
      : `The ${count} ${count === 1 ? 'user' : 'users'} currently in the group will become a fixed list that you manage by hand. Membership will no longer update automatically as users’ details change, and the rule will be removed.`;

  const handleConfirm = async (): Promise<void> => {
    setSaving(true);
    const result = await memberGroupDetails.switchMembershipMode(MembershipMode.Manual);
    setSaving(false);
    if (result.ok) {
      notification.enqueueSuccessSnackbar('Switched to manual. Membership is now a fixed list.');
      onClose();
    } else {
      notification.enqueueErrorSnackbar(getErrorMessage(result.error, 'Could not change membership'));
    }
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      modalTitle="Change to manual membership?"
      modalDescription={description}
      buttonsSpacing="space-between"
      cancelButton={
        <Button variant="outlined" size="large" onClick={onClose}>
          Cancel
        </Button>
      }
      submitButton={
        <Button variant="contained" size="large" onClick={handleConfirm} disabled={saving}>
          Change to manual
        </Button>
      }
    />
  );
});

export default ConvertToManualModal;
